import JSZip from "jszip";
import { parseBlob } from "music-metadata";

const MAX_COMPRESSED_SIZE = 500 * 1024 * 1024; // 500MB
const MAX_UNCOMPRESSED_SIZE = 2 * 1024 * 1024 * 1024; // 2GB
const MAX_COMPRESSION_RATIO = 100;
const MAX_FILE_COUNT = 1000;

/**
 * Generates inventory metadata for soundkit ZIP files.
 * Includes security protections against zip bombs:
 * - Max compressed size check before extraction
 * - Max file count limit
 * - Progressive uncompressed size tracking (bails early if exceeded)
 * - Compression ratio validation
 */
export async function generateInventory(
  zipFile: File,
): Promise<Record<string, unknown>> {
  if (zipFile.size > MAX_COMPRESSED_SIZE) {
    throw new Error(
      `ZIP file too large. Maximum size: ${MAX_COMPRESSED_SIZE / (1024 * 1024)}MB`,
    );
  }

  const zip = await JSZip.loadAsync(zipFile);
  const files: Record<string, unknown> = {};
  const fileTypes: Record<string, number> = { wav: 0, mp3: 0 };
  let sizeUncompressed = 0;

  const audioFiles = Object.keys(zip.files).filter((filename) => {
    const file = zip.files[filename];
    if (file.dir) return false;

    const normalizedPath = filename.replace(/\\/g, "/");
    if (normalizedPath.includes("__MACOSX/")) return false;
    if (normalizedPath.split("/").some((part) => part.startsWith(".")))
      return false;
    if (normalizedPath.endsWith(".DS_Store")) return false;

    const ext = filename.split(".").pop()?.toLowerCase();
    return ext === "wav" || ext === "mp3";
  });

  if (audioFiles.length === 0) {
    throw new Error(
      "ZIP contains no audio files. Please include at least one WAV or MP3 file.",
    );
  }

  if (audioFiles.length > MAX_FILE_COUNT) {
    throw new Error(`Too many audio files in ZIP. Maximum: ${MAX_FILE_COUNT}`);
  }

  for (const filename of audioFiles) {
    const file = zip.files[filename];
    const blob = await file.async("blob");
    const ext = filename.split(".").pop()?.toLowerCase();

    // Check size limits during extraction, not after
    sizeUncompressed += blob.size;
    if (sizeUncompressed > MAX_UNCOMPRESSED_SIZE) {
      throw new Error(
        `Uncompressed size too large. Maximum: ${MAX_UNCOMPRESSED_SIZE / (1024 * 1024 * 1024)}GB`,
      );
    }

    const compressionRatio = sizeUncompressed / zipFile.size;
    if (compressionRatio > MAX_COMPRESSION_RATIO) {
      throw new Error(
        `Suspicious compression ratio detected (${Math.floor(compressionRatio)}:1). Possible zip bomb.`,
      );
    }

    let duration = 0;
    try {
      const metadata = await parseBlob(blob);
      duration = metadata.format.duration || 0;
    } catch {
      // Silently handle metadata parsing errors
    }

    files[filename] = {
      format_name: ext,
      duration: Number(duration.toFixed(4)),
      size: blob.size,
    };

    if (ext === "wav") fileTypes.wav++;
    if (ext === "mp3") fileTypes.mp3++;
  }

  return {
    summary: {
      "size-compressed": zipFile.size,
      "size-uncompressed": sizeUncompressed,
      "file-types": fileTypes,
    },
    files,
  };
}
